import { readDbgLocalFeaturesOverrides } from '../../dbg/feature';

const ALWAYS_DISABLED_FEATURE_VALUE = <const>false;
type AlwaysDisabledFeatureValue = typeof ALWAYS_DISABLED_FEATURE_VALUE;

export interface IBackendNewUserFeatures {
  header?: boolean;
  pricing?: boolean;
  workspaces?: boolean;
  workspacePayments?: boolean;
  workspaceInviteInFolders?: boolean;
  migrationToWorkspace?: boolean;
  replaceIntercomCall?: boolean;
  dragAndDrop?: boolean;
  proxyGroups?: AlwaysDisabledFeatureValue;
  hideTorAndFreeProxy?: AlwaysDisabledFeatureValue;
  pricingGeoProxyTraffic?: AlwaysDisabledFeatureValue;
  proxyGroupsV2?: boolean;
  hideTorAndFreeProxyV2?: boolean;
  pricingGeoProxyTrafficV2?: boolean;
  renameProxyToLocation: boolean;
  proxyEditForm?: boolean;
  proxyEditFormCities?: boolean;
  paddlePayments?: boolean;
  objectPool?: boolean;
}

export interface INewFeatures extends IBackendNewUserFeatures {
  themeSwitch?: boolean;
  importProxyFile?: boolean;
  templateProfileName?: boolean;
  newStripeCheckout?: boolean;
  proxyPagination?: boolean;
  showUpgrade?: boolean;
  differentProvider?: boolean;
  debugUtils?: boolean;
  addProxySimulateCronArchive: boolean;
}

const isDbgDomain = [
  'http://localhost:3000',
  'https://app-alpha.gologin.com',
].includes(window?.location?.origin);

const DEFAULT_NEW_FEATURES: INewFeatures = {
  header: true,
  pricing: false,
  themeSwitch: true,
  workspaces: true,
  workspacePayments: true,
  workspaceInviteInFolders: true,
  migrationToWorkspace: true,
  replaceIntercomCall: true,
  dragAndDrop: true,
  importProxyFile: true,
  templateProfileName: true,
  newStripeCheckout: true,
  proxyPagination: true,
  showUpgrade: true,
  proxyGroups: ALWAYS_DISABLED_FEATURE_VALUE,
  hideTorAndFreeProxy: ALWAYS_DISABLED_FEATURE_VALUE,
  pricingGeoProxyTraffic: ALWAYS_DISABLED_FEATURE_VALUE,
  proxyGroupsV2: true,
  hideTorAndFreeProxyV2: true,
  pricingGeoProxyTrafficV2: true,
  renameProxyToLocation: true,
  proxyEditForm: true,
  proxyEditFormCities: true,
  paddlePayments: false,
  objectPool: true,
  debugUtils: false,
  addProxySimulateCronArchive: true,
};

export let NEW_FEATURES: INewFeatures = { ...DEFAULT_NEW_FEATURES };

const getPreparedFeature = (feature: INewFeatures): INewFeatures => {
  let preparedFeature: INewFeatures = feature;
  
  if (feature.workspaces) {
    preparedFeature.header = true;
  }

  if (feature.header) {
    preparedFeature.templateProfileName = true;
  }

  if (feature.migrationToWorkspace) {
    preparedFeature.workspaces = true;
  }

  // drag and drop won't work on old design yet
  if (!feature.header) {
    preparedFeature.dragAndDrop = false;
  }

  // object pool shouldn't work without folder ids and similar stuff
  // temporarily it is also disabled on old design, for extra safety
  if (!(feature.dragAndDrop && feature.header)) {
    preparedFeature.objectPool = false;
  }

  // TODO: only enable on test domains or for specific users
  preparedFeature.debugUtils = DEFAULT_NEW_FEATURES.debugUtils;
  if (preparedFeature.debugUtils) {
    const overrides = readDbgLocalFeaturesOverrides();
    preparedFeature = { ...preparedFeature, ...overrides };

    // backend doesn't supply it, so it should be manually toggled here
    preparedFeature.addProxySimulateCronArchive = DEFAULT_NEW_FEATURES.addProxySimulateCronArchive;
  }

  return preparedFeature;
};

export const toggleNewUserFeature = (newFeatures: INewFeatures): void => {
  const keys: (keyof INewFeatures)[] = Object.keys(DEFAULT_NEW_FEATURES) as Array<keyof typeof DEFAULT_NEW_FEATURES>;
  const feature: INewFeatures = {};

  keys.forEach(key => {
    feature[key] = DEFAULT_NEW_FEATURES[key] && (newFeatures[key] ?? false);
  });

  const preparedFeature = getPreparedFeature(feature);
  NEW_FEATURES = {
    ...preparedFeature,
    newStripeCheckout: true,
    proxyGroups: ALWAYS_DISABLED_FEATURE_VALUE,
    hideTorAndFreeProxy: ALWAYS_DISABLED_FEATURE_VALUE,
    pricingGeoProxyTraffic: ALWAYS_DISABLED_FEATURE_VALUE,
  };
};

export const resetNewUserFeature = (): void => {
  NEW_FEATURES = {
    ...DEFAULT_NEW_FEATURES,
  };
};

export const setFeatures = (features: Partial<INewFeatures>): void => {
  NEW_FEATURES = {
    ...NEW_FEATURES,
    ...features,
  };
};

export const setFeaturesAndReload = (features: Partial<INewFeatures>): void => {
  setFeatures(features);
  window.location.reload();
};
